import { useState, useEffect } from "react";
import axios from "axios";
import {
  User, Phone, Mail, MapPin, Home, TreePine,
  ChevronRight, Check, Dog, Droplets, KeyRound,
  FileText, Leaf, CheckCircle, Building
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REFERRAL_OPTIONS = [
  { value: "", label: "How did you hear about us?" },
  { value: "google", label: "Google Search" },
  { value: "social_media", label: "Social Media" },
  { value: "work_app", label: "Work App" },
  { value: "referral", label: "Referral / Word of Mouth" },
];

function SplashScreen({ onDone }) {
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setFading(true), 1800);
    const remove = setTimeout(onDone, 2300);
    return () => { clearTimeout(timer); clearTimeout(remove); };
  }, [onDone]);

  return (
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center transition-opacity duration-500 ${fading ? "opacity-0" : "opacity-100"}`}
      style={{ background: "#0a0f0a" }}
    >
      <div className="text-center">
        <div className="gow-shimmer-text">GOW</div>
        <div className="mt-5 font-sans text-xs tracking-[0.35em] uppercase text-[#3d6b42] opacity-70">
          Georgia Outdoor Worx
        </div>
      </div>
    </div>
  );
}

function SuccessScreen() {
  return (
    <div className="min-h-screen bg-[#0a0f0a] flex items-center justify-center px-4" data-testid="triage-success">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-forest-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-forest-400" />
        </div>
        <h2 className="font-sans text-2xl font-bold text-white tracking-wide mb-3">
          Thank You!
        </h2>
        <p className="text-[#8aae8e] leading-relaxed mb-6">
          Your information has been received. A member of our team will contact you shortly to discuss your property needs.
        </p>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-sm text-[#5e8a62]">
            Need immediate assistance?
          </p>
          <a href="tel:+14784182629" className="inline-flex items-center gap-2 text-forest-300 hover:text-white font-semibold mt-1 transition-colors">
            <Phone className="w-4 h-4" />
            478.418.2629
          </a>
        </div>
        <a
          href="/"
          data-testid="triage-go-home"
          className="mt-6 inline-flex items-center justify-center gap-2 glass-button rounded-full px-10 py-4 text-base text-white font-serif tracking-wide transition-all hover:scale-105"
          style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", backdropFilter: "blur(12px)" }}
        >
          <Leaf className="w-5 h-5" />
          Visit Our Website
        </a>
      </div>
    </div>
  );
}

export default function TriagePage() {
  const [showSplash, setShowSplash] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    address: "", city: "", zip_code: "", usage_type: "", referral_source: "",
    has_pets: false, has_irrigation: false,
    gate_code: "", message: "", company_name: "",
  });

  const set = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const canProceed = form.name.trim().length >= 2 && form.phone.trim().length >= 7;
  const needsCompany = form.usage_type === "commercial";
  const hasAddress = form.address.trim().length >= 3;
  const hasCity = form.city.trim().length >= 2;
  const hasZip = form.zip_code.trim().length >= 3;
  const canSubmit = (!needsCompany || form.company_name.trim().length >= 2) && hasAddress && hasCity && hasZip;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/triage`, {
        ...form,
        lead_source: "manual_link",
      });
      setSubmitted(true);
    } catch (err) {
      alert("Something went wrong. Please try again or call us at 478.418.2629");
    } finally {
      setSubmitting(false);
    }
  };

  if (showSplash) return <SplashScreen onDone={() => setShowSplash(false)} />;
  if (submitted) return <SuccessScreen />;

  return (
    <div className="min-h-screen bg-[#0a0f0a] flex items-center justify-center p-4 md:p-8" data-testid="triage-page">
      <style>{`
        .gow-shimmer-text {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          font-weight: 700; font-size: clamp(4rem, 15vw, 10rem);
          letter-spacing: 0.15em; line-height: 1;
          background: linear-gradient(90deg, #2F5233 0%, #4a8b52 20%, #d4e8d0 40%, #f0f7ee 50%, #d4e8d0 60%, #4a8b52 80%, #2F5233 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          animation: gow-shimmer 2s ease-in-out infinite;
        }
        @keyframes gow-shimmer { 0% { background-position: 100% 50%; } 100% { background-position: -100% 50%; } }
      `}</style>

      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Leaf className="w-6 h-6 text-forest-400" />
            <span className="font-sans text-lg font-bold text-white tracking-wide">Georgia Outdoor Worx</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {step === 1 ? "Let's Get Started" : "Property Details"}
          </h1>
          <p className="text-sm text-[#6b9b6f]">
            {step === 1 ? "Tell us who you are" : "Help us understand your property"}
          </p>
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mt-5">
            <div className={`h-1 w-16 rounded-full transition-colors ${step >= 1 ? "bg-forest-500" : "bg-white/10"}`} />
            <div className={`h-1 w-16 rounded-full transition-colors ${step >= 2 ? "bg-forest-500" : "bg-white/10"}`} />
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1 */}
          {step === 1 && (
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5 backdrop-blur-sm" data-testid="triage-step1">
              <div>
                <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7a4e]" />
                  <input
                    data-testid="triage-name"
                    value={form.name} onChange={set("name")}
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500/50 transition-all"
                    placeholder="John Smith" required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Phone / WhatsApp *</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7a4e]" />
                  <input
                    data-testid="triage-phone"
                    value={form.phone} onChange={set("phone")} type="tel"
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500/50 transition-all"
                    placeholder="(478) 555-1234" required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7a4e]" />
                  <input
                    data-testid="triage-email"
                    value={form.email} onChange={set("email")} type="email"
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500/50 transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <button
                type="button"
                data-testid="triage-proceed"
                disabled={!canProceed}
                onClick={() => setStep(2)}
                className="w-full flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-500 disabled:opacity-30 disabled:cursor-not-allowed text-white py-3.5 rounded-xl font-semibold text-sm tracking-wide transition-all"
              >
                Proceed <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5 backdrop-blur-sm" data-testid="triage-step2">
              {/* Confirmed identity */}
              <div className="bg-forest-500/10 border border-forest-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
                <Check className="w-4 h-4 text-forest-400 flex-shrink-0" />
                <span className="text-sm text-forest-300">{form.name} — {form.phone}</span>
                <button type="button" onClick={() => setStep(1)} className="ml-auto text-xs text-forest-400/60 hover:text-forest-300 underline">Edit</button>
              </div>

              {/* Address */}
              <div>
                <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Property Address *</label>
                <div className="relative">
                  <MapPin className="absolute left-3.5 top-3 w-4 h-4 text-[#4a7a4e]" />
                  <input
                    data-testid="triage-address"
                    required
                    value={form.address} onChange={set("address")}
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all"
                    placeholder="123 Main St"
                  />
                </div>
              </div>

              {/* City + Zip Code */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">City *</label>
                  <input
                    data-testid="triage-city"
                    required
                    value={form.city} onChange={set("city")}
                    className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all"
                    placeholder="Macon"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Zip Code *</label>
                  <input
                    data-testid="triage-zip"
                    required
                    value={form.zip_code} onChange={set("zip_code")}
                    inputMode="numeric"
                    pattern="[0-9\-]*"
                    maxLength={10}
                    className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all"
                    placeholder="31201"
                  />
                </div>
              </div>

              {/* Usage Type + Referral */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Usage Type</label>
                  <div className="flex gap-2">
                    <button type="button" data-testid="triage-residential"
                      onClick={() => setForm(f => ({ ...f, usage_type: "residential", company_name: "" }))}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                        form.usage_type === "residential"
                          ? "bg-forest-500/20 border-forest-500/50 text-forest-300"
                          : "bg-white/[0.04] border-white/10 text-[#6b8a6f] hover:border-white/20"
                      }`}
                    >
                      <Home className="w-3.5 h-3.5" /> Residential
                    </button>
                    <button type="button" data-testid="triage-commercial"
                      onClick={() => setForm(f => ({ ...f, usage_type: "commercial" }))}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                        form.usage_type === "commercial"
                          ? "bg-forest-500/20 border-forest-500/50 text-forest-300"
                          : "bg-white/[0.04] border-white/10 text-[#6b8a6f] hover:border-white/20"
                      }`}
                    >
                      <TreePine className="w-3.5 h-3.5" /> Commercial
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">How did you find us?</label>
                  <select
                    data-testid="triage-referral"
                    value={form.referral_source} onChange={set("referral_source")}
                    className="w-full px-3 py-2.5 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all appearance-none"
                  >
                    {REFERRAL_OPTIONS.map(o => <option key={o.value} value={o.value} className="bg-[#1a1f1a] text-white">{o.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Company Name (commercial only) */}
              {needsCompany && (
                <div className="transition-all duration-300 ease-out" data-testid="company-name-field">
                  <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Company Name *</label>
                  <div className="relative">
                    <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7a4e]" />
                    <input
                      data-testid="triage-company"
                      value={form.company_name} onChange={set("company_name")}
                      className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all"
                      placeholder="Company Name" required
                    />
                  </div>
                </div>
              )}

              {/* Checkboxes */}
              <div className="grid grid-cols-2 gap-4">
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  form.has_pets ? "bg-forest-500/10 border-forest-500/30" : "bg-white/[0.03] border-white/10 hover:border-white/20"
                }`}>
                  <input type="checkbox" data-testid="triage-pets" checked={form.has_pets} onChange={set("has_pets")} className="sr-only" />
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                    form.has_pets ? "bg-forest-500 border-forest-500" : "border-white/20"
                  }`}>
                    {form.has_pets && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <Dog className="w-4 h-4 text-[#6b9b6f]" />
                  <span className="text-sm text-[#a0bba3]">Has Pets</span>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  form.has_irrigation ? "bg-forest-500/10 border-forest-500/30" : "bg-white/[0.03] border-white/10 hover:border-white/20"
                }`}>
                  <input type="checkbox" data-testid="triage-irrigation" checked={form.has_irrigation} onChange={set("has_irrigation")} className="sr-only" />
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                    form.has_irrigation ? "bg-forest-500 border-forest-500" : "border-white/20"
                  }`}>
                    {form.has_irrigation && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <Droplets className="w-4 h-4 text-[#6b9b6f]" />
                  <span className="text-sm text-[#a0bba3]">Has Irrigation</span>
                </label>
              </div>

              {/* Gate Code */}
              <div>
                <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Gate Code</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7a4e]" />
                  <input
                    data-testid="triage-gate"
                    value={form.gate_code} onChange={set("gate_code")}
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all"
                    placeholder="Enter gate code if applicable"
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div>
                <label className="text-xs font-medium text-[#6b9b6f] uppercase tracking-wider mb-2 block">Additional Information</label>
                <div className="relative">
                  <FileText className="absolute left-3.5 top-3 w-4 h-4 text-[#4a7a4e]" />
                  <textarea
                    data-testid="triage-message"
                    value={form.message} onChange={set("message")}
                    rows={4}
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-[#4a5e4c] text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-all resize-none"
                    placeholder="Describe your property needs, special requests, or any relevant details..."
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                data-testid="triage-submit"
                disabled={submitting || !canSubmit}
                className="w-full flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 rounded-xl font-semibold text-sm tracking-wide transition-all"
              >
                {submitting ? "Sending..." : "Submit Information"}
              </button>

              <button type="button" onClick={() => setStep(1)} className="w-full text-center text-xs text-[#5e7a5f] hover:text-[#8aae8e] transition-colors">
                Go back to step 1
              </button>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-[#3d5a3f]">
          &copy; {new Date().getFullYear()} Georgia Outdoor Worx. All rights reserved.
        </div>
      </div>
    </div>
  );
}
